import { Box, Stack } from "@mui/material";
import { Custom, Field, Workflow } from "components";
import { useApp, useData, useTheme } from "contexts";
import { Auxiliars, Combines, Conversions } from "helpers";
import { IPreference } from "interfaces";
import { createStep } from "utils/helpers";
import { useWorkflow } from "contexts/components";
import { forwardRef } from "react";

// icons
import { InfoRounded } from "@mui/icons-material";

const StepUser = forwardRef(() => {
  const { nextStep } = useWorkflow<Workflow.Utils.ProfileType>();
  const { t } = useApp();
  const { user } = useData();
  const { theme } = useTheme();

  return (
    <Stack spacing={theme.spacing.sm}>
      {/* header and edit */}
      <Stack direction="row" justifyContent="space-between">
        <Custom.Typography
          size={{
            xs: theme.font.sm,
            sm: theme.font.md,
            md: theme.font.xl,
          }}
          weight={theme.font.bold}
        >
          {Combines.interpolate(t.message.greeting[Auxiliars.greeting()], {
            username: user.full_name,
          })}
        </Custom.Typography>
        <Custom.Button size="small" trailing="edit" onClick={nextStep}>
          {t.action.edit}
        </Custom.Button>
      </Stack>
      {/* avatar and details */}
      <Box width={1} display="flex">
        <Stack
          width={1}
          ml={{ md: theme.spacing.xxl }}
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          spacing={theme.spacing.xxl}
        >
          <Field.UploadableAvatar src={user._id} alt={user.full_name} />
          <Box
            sx={{
              flex: 1,
              width: 1,
              height: 1,
              p: theme.spacing.md,
              borderRadius: theme.border.radius,
              backgroundColor: theme.color.background.accent,
            }}
          >
            <Stack spacing={theme.spacing.sm}>
              {/* user details */}
              <Stack
                direction="row"
                flexWrap="wrap"
                spacing={theme.spacing.sm}
                sx={{
                  "& > span:not(:last-child):after": {
                    content: "'•'",
                    ml: theme.spacing.sm,
                  },
                }}
              >
                {[
                  user.full_name,
                  user.birth_date
                    ? `Age ${Conversions.toAge(user.birth_date)}`
                    : "",
                  user.city && user.country
                    ? `${user.city}, ${user.country}`
                    : "",
                ]
                  .filter(Boolean)
                  .map((item: string, i: number) => {
                    return (
                      <Custom.Typography
                        key={i}
                        size={theme.font.md}
                        weight={theme.font.bold}
                      >
                        {item}
                      </Custom.Typography>
                    );
                  })}
              </Stack>
              {/* allergies */}
              <Stack direction="row" spacing={theme.spacing.xs}>
                <InfoRounded
                  sx={{
                    color:
                      theme.color.status[
                        user.allergy.length > 0 ? "error" : "success"
                      ].color,
                  }}
                />
                <Custom.Typography
                  size={theme.font.xs}
                  weight={theme.font.semiBold}
                  color={
                    theme.color.status[
                      user.allergy.length > 0 ? "error" : "success"
                    ].color
                  }
                >
                  {user.allergy.join(", ") || t.message.placeholder.no_allergy}
                </Custom.Typography>
              </Stack>
              {/* preferences */}
              {user.preferences.length > 0 && (
                <Custom.Typography size={theme.font.sm}>
                  {t.component.common.profile.preference_prefix}{" "}
                  {user.preferences
                    .map((item: IPreference) => item.name.toLowerCase())
                    .join(", ")}
                </Custom.Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
      {/* dietary preference */}
      <Box
        sx={{
          flex: 1,
          width: 1,
          height: 1,
          p: theme.spacing.md,
          borderRadius: theme.border.radius,
          backgroundColor: theme.color.background.accent,
        }}
      >
        <Stack spacing={theme.spacing.xs} alignItems="center">
          <Custom.Typography size={theme.font.sm} weight={theme.font.bold}>
            {t.component.common.profile.my_dietary_preference}
          </Custom.Typography>
          <Custom.Typography size={theme.font.sm} weight={theme.font.medium}>
            {user.dietary_preference}
          </Custom.Typography>
          {user.dietary_preference.length === 0 && (
            <Custom.Typography>
              {t.message.placeholder.nothing_to_display}
            </Custom.Typography>
          )}
        </Stack>
      </Box>
      <Box width={1} display="flex">
        <Stack
          width={1}
          spacing={theme.spacing.md}
          direction={{ xs: "column", sm: "row" }}
        >
          {/* favorite cuisines */}
          <Box
            sx={{
              flex: 1,
              width: 1,
              height: 1,
              p: theme.spacing.md,
              borderRadius: theme.border.radius,
              backgroundColor: theme.color.background.accent,
            }}
          >
            <Stack spacing={theme.spacing.xs} alignItems="center">
              <Custom.Typography size={theme.font.sm} weight={theme.font.bold}>
                {t.component.common.profile.my_favorite_cuisine}
              </Custom.Typography>
              <Stack direction="row" flexWrap="wrap" gap={theme.spacing.sm}>
                {user.cuisines.map((item: string, i: number) => {
                  return <Custom.Chip key={i} label={item} />;
                })}
                {user.cuisines.length === 0 && (
                  <Custom.Typography>
                    {t.message.placeholder.nothing_to_display}
                  </Custom.Typography>
                )}
              </Stack>
            </Stack>
          </Box>
          {/* no go's */}
          <Box
            sx={{
              flex: 1,
              width: 1,
              height: 1,
              p: theme.spacing.md,
              borderRadius: theme.border.radius,
              backgroundColor: theme.color.background.accent,
            }}
          >
            <Stack spacing={theme.spacing.xs} alignItems="center">
              <Custom.Typography size={theme.font.sm} weight={theme.font.bold}>
                {t.component.common.profile.no_goes}
              </Custom.Typography>{" "}
              <Stack direction="row" flexWrap="wrap" gap={theme.spacing.sm}>
                {user.no_go.map((item: string, i: number) => {
                  return <Custom.Chip key={i} label={item} />;
                })}
                {user.no_go.length === 0 && (
                  <Custom.Typography>
                    {t.message.placeholder.nothing_to_display}
                  </Custom.Typography>
                )}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
});

export default createStep<Workflow.Utils.ProfileType>(StepUser);
